import {
  Alert,
  AlertExplanationContext,
  AlertSection,
  AlertTrigger,
  AlertType,
  AlertsCommentary,
  DismissedAlert,
  RouteItem,
  alertRequiresNavAttention,
  dismissedAlertMatches,
  toDismissedAlertEntry,
} from '@aloft/toolkit-shared';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { PropertyGridCellKey, PropertyGridType } from '../../components/PropertyGrid/types.ts';
import {
  computeTriggeredAlertsCountBySection,
  getActiveAlertsForRoute,
  getTriggeredAlertsForRoute,
} from '../../hooks/validation/useAppraisifyNavigation.ts';
import { EDIT_MODE_OFF } from '../../modules/analysis/consts.ts';
import { getDeviceWidthFromWindowWidth } from '../nullComponents/DeviceWidthDriver/getDeviceWidthFromWindowWidth.ts';
import { AIV_ARV_TABS, ActiveTour, AivArvActiveTab, DeviceWidth, UIState } from '../types.ts';
import { removeComp, removeComps } from './actions/removeComp.ts';
import { resolveFeatureGridFieldConflicts } from './actions/resolveFeatureGridFieldConflicts.ts';
import { DismissAlertPayload } from './alertsSlice.ts';
import { setOrder } from './orderSlice.ts';

const filterConflictedFieldsForRemovedProperties = (
  fields: UIState['featureGridConflictedFields'],
  propertyIds: readonly string[],
): UIState['featureGridConflictedFields'] =>
  Object.fromEntries(
    Object.entries(fields).filter(([, value]) => !propertyIds.includes(value.propertyId)),
  );

const filterDismissedEntriesForRemovedProperties = (
  entries: DismissedAlert[],
  propertyIds: readonly string[],
): DismissedAlert[] =>
  entries.filter(
    (entry) => typeof entry === 'string' || !propertyIds.includes(entry.propertyID),
  );

const ANALYSIS_TOUR_LENGTH = 14;

export const tourLengths: Record<ActiveTour, number> = { analysisTour: ANALYSIS_TOUR_LENGTH };

const AIV_ARV_TAB_STORAGE_KEY_PREFIX = 'aivArvActiveTab';

const DEFAULT_AIV_ARV_TAB: AivArvActiveTab = 'all';

export const getAivArvTabStorageKey = (orderId: string): string =>
  `${AIV_ARV_TAB_STORAGE_KEY_PREFIX}-${orderId}`;

const loadAivArvTabFromLocalStorage = (orderId: string): AivArvActiveTab => {
  if (!orderId) {
    return DEFAULT_AIV_ARV_TAB;
  }
  try {
    const storageKey = getAivArvTabStorageKey(orderId);
    const stored = window.localStorage.getItem(storageKey);
    if (stored && AIV_ARV_TABS.includes(stored as AivArvActiveTab)) {
      return stored as AivArvActiveTab;
    }
  } catch {
    // Ignore localStorage errors (e.g., in private browsing mode)
  }
  return DEFAULT_AIV_ARV_TAB;
};

const applyAlertTriggerMatch = (
  state: UIState,
  trigger: AlertTrigger,
  alertsPerSection: Record<AlertSection, Alert[]>,
  alertsCommentary?: AlertsCommentary,
  explanationContext?: AlertExplanationContext,
) => {
  const matchesTrigger = (alert: Alert) =>
    alert.triggers.includes(trigger) &&
    alertRequiresNavAttention(alert, alertsCommentary, explanationContext);
  const newTriggeredAlerts = Object.values(alertsPerSection)
    .flat()
    .filter(matchesTrigger)
    .map((alert) => alert.type);
  const newTriggeredAlertsCountBySection = Object.fromEntries(
    Object.entries(alertsPerSection).map(([section, alerts]) => [
      section,
      alerts.filter(matchesTrigger).length,
    ]),
  ) as Record<AlertSection, number>;

  Object.assign(state, {
    triggeredAlertsCountBySection: newTriggeredAlertsCountBySection,
    triggeredAlerts: [...state.triggeredAlerts, ...newTriggeredAlerts].filter(
      (value, index, self) => self.indexOf(value) === index,
    ),
  });
};

export const initialUIState: UIState = {
  analysisTourIndex: -1,
  activeTour: null,
  activeDepreciateSeparatelyID: EDIT_MODE_OFF,
  deviceWidth: getDeviceWidthFromWindowWidth(),
  isEditPropertyDetailsModalOpen: false,
  objectiveEditMode: EDIT_MODE_OFF,
  propertyGridExpandedPropertyIds: {},
  recommendedCompsStatusGroups: {
    sold: { collapsed: false },
    active: { collapsed: false },
    pending: { collapsed: false },
  },
  featureGridActiveConflictedFields: {},
  featureGridConflictedFields: {},
  selectedPriorSalesPropertyId: 'subject',
  aivArvActiveTab: DEFAULT_AIV_ARV_TAB,
  escalationBypassed: false,
  stopAndCallManualIssue: undefined,
  triggeredAlerts: [],
  triggeredAlertsCountBySection: {
    adjustments: 0,
    depreciatedCost: 0,
    comparable: 0,
  },
  toastSuppressedAlertTypes: [],
  pendingNavBlockToastTypes: [],
  pendingDismissals: [],
  finalizedExplanations: [],
};

const uiSlice = createSlice({
  name: 'ui',
  initialState: initialUIState,
  reducers: {
    setActiveTour(state, action: PayloadAction<{ activeTour: UIState['activeTour'] }>) {
      Object.assign(state, { activeTour: action.payload.activeTour });
    },
    incrementActiveTourIndex(state, _action: PayloadAction) {
      switch (state.activeTour) {
        case 'analysisTour':
          if (state.analysisTourIndex < ANALYSIS_TOUR_LENGTH - 1) {
            Object.assign(state, { analysisTourIndex: state.analysisTourIndex + 1 });
          }

          break;
        default:
          break;
      }
    },
    decrementActiveTourIndex(state, _action: PayloadAction) {
      switch (state.activeTour) {
        case 'analysisTour':
          if (state.analysisTourIndex > 0)
            Object.assign(state, { analysisTourIndex: state.analysisTourIndex - 1 });

          break;
        default:
          break;
      }
    },
    resetActiveTourIndex(state, _action: PayloadAction) {
      switch (state.activeTour) {
        case 'analysisTour':
          Object.assign(state, { analysisTourIndex: -1 });
          break;
        default:
          break;
      }
    },
    setActiveDepreciateSeparatelyID(state, action: PayloadAction<string>) {
      Object.assign(state, { activeDepreciateSeparatelyID: action.payload });
    },
    setIsEditPropertyDetailsModalOpen(state, action: PayloadAction<boolean>) {
      Object.assign(state, { isEditPropertyDetailsModalOpen: action.payload });
    },
    setObjectiveEditMode(state, action: PayloadAction<string>) {
      Object.assign(state, {
        objectiveEditMode:
          state.objectiveEditMode === action.payload ? EDIT_MODE_OFF : action.payload,
      });
    },
    setDeviceWidth_DANGER_INTERNAL(state, action: PayloadAction<{ deviceWidth: DeviceWidth }>) {
      Object.assign(state, { deviceWidth: action.payload.deviceWidth });
    },
    setPropertyGridExpandedPropertyId(
      state,
      action: PayloadAction<{ gridType: PropertyGridType; propertyId: string | undefined }>,
    ) {
      const { gridType, propertyId } = action.payload;
      Object.assign(state, {
        propertyGridExpandedPropertyIds: {
          ...state.propertyGridExpandedPropertyIds,
          [gridType]: propertyId,
        },
      });
    },
    toggleRecommendedCompsStatusGroup(
      state,
      action: PayloadAction<'sold' | 'active' | 'pending'>,
    ) {
      const group = action.payload;
      Object.assign(state, {
        recommendedCompsStatusGroups: {
          ...state.recommendedCompsStatusGroups,
          [group]: {
            collapsed: !state.recommendedCompsStatusGroups[group].collapsed,
          },
        },
      });
    },
    setSelectedPriorSalesPropertyId(state, action: PayloadAction<string>) {
      Object.assign(state, { selectedPriorSalesPropertyId: action.payload });
    },
    setAivArvActiveTab(state, action: PayloadAction<UIState['aivArvActiveTab']>) {
      Object.assign(state, { aivArvActiveTab: action.payload });
    },
    addFeatureGridConflictedField(
      state,
      action: PayloadAction<UIState['featureGridActiveConflictedFields']>,
    ) {
      const newActive = { ...state.featureGridActiveConflictedFields };
      const newAll = { ...state.featureGridConflictedFields };
      Object.entries(action.payload).forEach(([key, value]) => {
        if (!Object.keys(newActive).includes(key)) {
          newActive[key as keyof UIState['featureGridActiveConflictedFields']] = value;
        }
        if (!Object.keys(newAll).includes(key)) {
          newAll[key as keyof UIState['featureGridConflictedFields']] = value;
        }
      });

      Object.assign(state, {
        featureGridActiveConflictedFields: newActive,
        featureGridConflictedFields: newAll,
      });
    },
    bypassEscalation(state, _action: PayloadAction) {
      Object.assign(state, { escalationBypassed: true });
    },
    setStopAndCallManualIssue(state, action: PayloadAction<UIState['stopAndCallManualIssue']>) {
      Object.assign(state, { stopAndCallManualIssue: action.payload });
    },
    setFocusedPropertyGridCellKey(
      state,
      action: PayloadAction<
        | PropertyGridCellKey
        | undefined
        | { key: PropertyGridCellKey | undefined; activate?: boolean }
      >,
    ) {
      const { payload } = action;
      if (payload === undefined || typeof payload === 'string') {
        Object.assign(state, {
          focusedPropertyGridCellKey: payload,
          focusedOrderDetailsFieldKey: undefined,
          focusedRequiredFieldKey: undefined,
          focusedCostApproachPropertyId: undefined,
          ...(payload === undefined && { propertyGridCellKeyPendingActivation: undefined }),
        });
        return;
      }

      Object.assign(state, {
        focusedPropertyGridCellKey: payload.key,
        focusedOrderDetailsFieldKey: undefined,
        focusedRequiredFieldKey: undefined,
        focusedCostApproachPropertyId: undefined,
        propertyGridCellKeyPendingActivation:
          payload.activate && payload.key ? payload.key : undefined,
      });
    },
    setFocusedOrderDetailsFieldKey(state, action: PayloadAction<string | undefined>) {
      state.focusedOrderDetailsFieldKey = action.payload;
      if (action.payload) {
        state.focusedPropertyGridCellKey = undefined;
        state.propertyGridCellKeyPendingActivation = undefined;
        state.focusedRequiredFieldKey = undefined;
        state.focusedCostApproachPropertyId = undefined;
      }
    },
    setFocusedRequiredFieldKey(state, action: PayloadAction<string | undefined>) {
      state.focusedRequiredFieldKey = action.payload;
      if (action.payload) {
        state.focusedPropertyGridCellKey = undefined;
        state.propertyGridCellKeyPendingActivation = undefined;
        state.focusedOrderDetailsFieldKey = undefined;
        state.focusedCostApproachPropertyId = undefined;
      }
    },
    setFocusedCostApproachPropertyId(state, action: PayloadAction<string | undefined>) {
      state.focusedCostApproachPropertyId = action.payload;
      if (action.payload) {
        state.focusedPropertyGridCellKey = undefined;
        state.focusedOrderDetailsFieldKey = undefined;
        state.focusedRequiredFieldKey = undefined;
        state.propertyGridCellKeyPendingActivation = undefined;
      }
    },
    clearFocusedCostApproachPropertyId(state, _action: PayloadAction) {
      state.focusedCostApproachPropertyId = undefined;
    },
    clearPropertyGridCellKeyPendingActivation(state) {
      state.propertyGridCellKeyPendingActivation = undefined;
    },
    clearTriggeredAlerts(state, _action: PayloadAction) {
      Object.assign(state, { triggeredAlerts: [] });
    },
    suppressAlertToasts(state, action: PayloadAction<AlertType[]>) {
      state.toastSuppressedAlertTypes = [
        ...state.toastSuppressedAlertTypes,
        ...action.payload,
      ].filter((value, index, self) => self.indexOf(value) === index);
    },
    clearToastSuppressedAlertTypes(state, _action: PayloadAction) {
      state.toastSuppressedAlertTypes = [];
    },
    unsuppressAlertToast(state, action: PayloadAction<AlertType>) {
      state.toastSuppressedAlertTypes = state.toastSuppressedAlertTypes.filter(
        (type) => type !== action.payload,
      );
    },
    retriggerAlertToasts(state, action: PayloadAction<AlertType[]>) {
      const typesToRetrigger = new Set(action.payload);
      state.pendingNavBlockToastTypes = action.payload.filter(
        (value, index, self) => self.indexOf(value) === index,
      );
      state.toastSuppressedAlertTypes = state.toastSuppressedAlertTypes.filter(
        (type) => !typesToRetrigger.has(type),
      );
    },
    clearPendingNavBlockToastTypes(state, _action: PayloadAction) {
      state.pendingNavBlockToastTypes = [];
    },
    addPendingDismissal(state, action: PayloadAction<DismissAlertPayload>) {
      const entry = toDismissedAlertEntry(action.payload);
      if (
        state.pendingDismissals.some((pending: DismissedAlert) =>
          dismissedAlertMatches(pending, action.payload),
        )
      ) {
        return;
      }
      state.pendingDismissals = [...state.pendingDismissals, entry];
    },
    removePendingDismissal(state, action: PayloadAction<DismissAlertPayload>) {
      state.pendingDismissals = state.pendingDismissals.filter(
        (pending: DismissedAlert) => !dismissedAlertMatches(pending, action.payload),
      );
    },
    addFinalizedExplanation(state, action: PayloadAction<DismissAlertPayload>) {
      const entry = toDismissedAlertEntry(action.payload);
      if (
        state.finalizedExplanations.some((finalized: DismissedAlert) =>
          dismissedAlertMatches(finalized, action.payload),
        )
      ) {
        return;
      }
      state.finalizedExplanations = [...state.finalizedExplanations, entry];
    },
    removeFinalizedExplanation(state, action: PayloadAction<DismissAlertPayload>) {
      state.finalizedExplanations = state.finalizedExplanations.filter(
        (finalized: DismissedAlert) => !dismissedAlertMatches(finalized, action.payload),
      );
    },
    clearOffPageTriggeredAlerts(
      state,
      action: PayloadAction<{
        route: RouteItem;
        alertsPerSection: Record<AlertSection, Alert[]>;
        alertsCommentary?: AlertsCommentary;
        explanationContext?: AlertExplanationContext;
      }>,
    ) {
      const { route, alertsPerSection, alertsCommentary, explanationContext } = action.payload;
      const currentPageTriggered = getTriggeredAlertsForRoute(
        state.triggeredAlerts,
        alertsPerSection,
        route,
      );

      const triggeredUnchanged =
        currentPageTriggered.length === state.triggeredAlerts.length &&
        currentPageTriggered.every((type) => state.triggeredAlerts.includes(type));
      if (triggeredUnchanged) {
        return;
      }

      Object.assign(state, {
        triggeredAlerts: currentPageTriggered,
        triggeredAlertsCountBySection: computeTriggeredAlertsCountBySection(
          alertsPerSection,
          currentPageTriggered,
          alertsCommentary,
          explanationContext,
        ),
      });
    },
    clearTriggeredAlert(state, action: PayloadAction<AlertType>) {
      const alertTypeToClear = action.payload;
      const newTriggeredAlerts = state.triggeredAlerts.filter(
        (alert) => alert !== alertTypeToClear,
      );
      const newTriggeredAlertsCountBySection = Object.fromEntries(
        Object.entries(state.triggeredAlertsCountBySection).map(([section, count]) => [
          section,
          alertTypeToClear && state.triggeredAlerts.includes(alertTypeToClear) ? count - 1 : count,
        ]),
      ) as Record<AlertSection, number>;

      Object.assign(state, {
        triggeredAlerts: newTriggeredAlerts,
        triggeredAlertsCountBySection: newTriggeredAlertsCountBySection,
      });
    },
    processAlertTrigger(
      state,
      action: PayloadAction<{
        trigger: AlertTrigger;
        alertsPerSection: Record<AlertSection, Alert[]>;
        alertsCommentary?: AlertsCommentary;
        explanationContext?: AlertExplanationContext;
      }>,
    ) {
      const { trigger, alertsPerSection, alertsCommentary, explanationContext } = action.payload;
      applyAlertTriggerMatch(
        state,
        trigger,
        alertsPerSection,
        alertsCommentary,
        explanationContext,
      );
    },
    processAlertsForRoute(
      state,
      action: PayloadAction<{
        route: RouteItem;
        alertsPerSection: Record<AlertSection, Alert[]>;
        alertsCommentary?: AlertsCommentary;
        explanationContext?: AlertExplanationContext;
        openAlertsPanel?: boolean;
      }>,
    ) {
      const { route, alertsPerSection, alertsCommentary, explanationContext } = action.payload;
      const matchingAlerts = getActiveAlertsForRoute(
        alertsPerSection,
        route,
        alertsCommentary,
        false,
        explanationContext,
      );
      if (matchingAlerts.length === 0) {
        return;
      }

      const matchingTypes = matchingAlerts.map((alert) => alert.type);
      const newTriggeredAlertsCountBySection = Object.fromEntries(
        Object.entries(alertsPerSection).map(([section, alerts]) => [
          section,
          alerts.filter(
            (alert) =>
              matchingTypes.includes(alert.type) &&
              alertRequiresNavAttention(alert, alertsCommentary, explanationContext),
          ).length,
        ]),
      ) as Record<AlertSection, number>;

      Object.assign(state, {
        triggeredAlertsCountBySection: {
          ...state.triggeredAlertsCountBySection,
          ...Object.fromEntries(
            Object.entries(newTriggeredAlertsCountBySection).map(([section, count]) => [
              section,
              state.triggeredAlertsCountBySection[section as AlertSection] + count,
            ]),
          ),
        },
        triggeredAlerts: [...state.triggeredAlerts, ...matchingTypes].filter(
          (value, index, self) => self.indexOf(value) === index,
        ),
      });
    },
  },

  extraReducers: (builder) => {
    builder.addCase(resolveFeatureGridFieldConflicts, (state, action) => {
      Object.assign(state, {
        featureGridActiveConflictedFields: Object.fromEntries(
          Object.entries(state.featureGridActiveConflictedFields).filter(
            ([key, _]) => !Object.keys(action.payload).includes(key),
          ),
        ),
      });
    });
    const clearStateForRemovedProperties = (state: UIState, propertyIds: readonly string[]) => {
      Object.assign(state, {
        featureGridConflictedFields: filterConflictedFieldsForRemovedProperties(
          state.featureGridConflictedFields,
          propertyIds,
        ),
        featureGridActiveConflictedFields: filterConflictedFieldsForRemovedProperties(
          state.featureGridActiveConflictedFields,
          propertyIds,
        ),
        pendingDismissals: filterDismissedEntriesForRemovedProperties(
          state.pendingDismissals,
          propertyIds,
        ),
        finalizedExplanations: filterDismissedEntriesForRemovedProperties(
          state.finalizedExplanations,
          propertyIds,
        ),
      });
    };
    builder.addCase(removeComp, (state, action) => {
      clearStateForRemovedProperties(state, [action.payload]);
    });
    builder.addCase(removeComps, (state, action) => {
      clearStateForRemovedProperties(state, action.payload.ids);
    });
    builder.addCase(setOrder, (state, action) => {
      // Load the order-specific tab selection from localStorage when order changes
      const orderId = action.payload;
      const savedTab = loadAivArvTabFromLocalStorage(orderId);
      Object.assign(state, {
        analysisTourIndex: -1,
        activeTour: null,
        activeDepreciateSeparatelyID: EDIT_MODE_OFF,
        isEditPropertyDetailsModalOpen: false,
        objectiveEditMode: EDIT_MODE_OFF,
        propertyGridExpandedPropertyIds: {},
        recommendedCompsStatusGroups: {
          sold: { collapsed: false },
          active: { collapsed: false },
          pending: { collapsed: false },
        },
        selectedPriorSalesPropertyId: 'subject',
        focusedPropertyGridCellKey: undefined,
        propertyGridCellKeyPendingActivation: undefined,
        focusedCostApproachPropertyId: undefined,
        aivArvActiveTab: savedTab,
        escalationBypassed: false,
        stopAndCallManualIssue: undefined,
        triggeredAlerts: [],
        triggeredAlertsCountBySection: {
          adjustments: 0,
          depreciatedCost: 0,
          comparable: 0,
        },
        toastSuppressedAlertTypes: [],
        pendingNavBlockToastTypes: [],
        pendingDismissals: [],
        finalizedExplanations: [],
        featureGridConflictedFields: {},
        featureGridActiveConflictedFields: {},
      });
    });
  },
});

export const {
  setActiveTour,
  incrementActiveTourIndex,
  decrementActiveTourIndex,
  resetActiveTourIndex,
  setActiveDepreciateSeparatelyID,
  setIsEditPropertyDetailsModalOpen,
  setObjectiveEditMode,
  setDeviceWidth_DANGER_INTERNAL,
  setPropertyGridExpandedPropertyId,
  toggleRecommendedCompsStatusGroup,
  setSelectedPriorSalesPropertyId,
  setAivArvActiveTab,
  addFeatureGridConflictedField,
  bypassEscalation,
  setStopAndCallManualIssue,
  setFocusedPropertyGridCellKey,
  setFocusedOrderDetailsFieldKey,
  setFocusedRequiredFieldKey,
  setFocusedCostApproachPropertyId,
  clearFocusedCostApproachPropertyId,
  clearPropertyGridCellKeyPendingActivation,
  clearTriggeredAlerts,
  clearToastSuppressedAlertTypes,
  suppressAlertToasts,
  retriggerAlertToasts,
  clearPendingNavBlockToastTypes,
  addPendingDismissal,
  removePendingDismissal,
  addFinalizedExplanation,
  removeFinalizedExplanation,
  unsuppressAlertToast,
  clearOffPageTriggeredAlerts,
  processAlertTrigger,
  processAlertsForRoute,
  clearTriggeredAlert,
} = uiSlice.actions;

export const uiReducer = uiSlice.reducer;
